
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ThumbsUp, Zap } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { findMatchingProviders, getMatchPercentage, getMatchReasoning } from '@/utils/pineconeUtils';
import { PEOProviderCard } from './peo/PEOProviderCard';
import type { MatchResult } from '@/types/pinecone.types';

interface AIRecommendedProvidersProps {
  applicationId: string;
}

export const AIRecommendedProviders = ({ applicationId }: AIRecommendedProvidersProps) => {
  const [runningMatch, setRunningMatch] = useState(false);
  
  const { data: matches, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['ai-recommended-providers', applicationId],
    queryFn: async () => {
      const result = await findMatchingProviders(applicationId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to find matching providers');
      }
      return result.matches || [];
    },
    enabled: false, // Don't run automatically, wait for user to click button
  });

  const handleRunAIMatch = async () => {
    setRunningMatch(true);
    try {
      await refetch();
    } catch (err) {
      console.error('Error running AI match:', err);
    } finally {
      setRunningMatch(false);
    }
  };

  if (isError) {
    return (
      <Card className="col-span-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" /> AI-Recommended Providers
          </CardTitle>
          <CardDescription>
            Get personalized PEO recommendations powered by AI
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error ? error.message : 'Failed to generate AI recommendations'}
            </AlertDescription>
          </Alert>
          <Button 
            onClick={handleRunAIMatch} 
            variant="outline" 
            className="mt-4"
            disabled={runningMatch}
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="col-span-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-primary" /> AI-Recommended Providers
        </CardTitle>
        <CardDescription>
          Get personalized PEO recommendations powered by AI
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!matches && !isLoading && (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-6">
              Our AI can analyze your application and find the PEO providers that best match your specific needs.
            </p>
            <Button 
              onClick={handleRunAIMatch} 
              className="gap-2"
              size="lg"
              disabled={runningMatch}
            >
              <Zap className="h-4 w-4" />
              {runningMatch ? 'Finding matches...' : 'Find My Best Matches'}
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="space-y-4">
            <p className="text-center text-muted-foreground mb-4">
              Analyzing your application and finding the best matches...
            </p>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Array(3).fill(0).map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-4 w-3/4 bg-muted rounded mb-4" />
                    <div className="space-y-2">
                      <div className="h-3 w-1/2 bg-muted rounded" />
                      <div className="h-3 w-2/3 bg-muted rounded" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {matches && matches.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                We found {matches.length} providers that match your needs
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRunAIMatch}
                disabled={runningMatch}
              >
                Refresh Matches
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {matches.map((match: MatchResult) => (
                <AIProviderCard 
                  key={match.providerId}
                  match={match}
                />
              ))}
            </div>
          </div>
        )}

        {matches && matches.length === 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              We couldn't find any strong matches for your application. Try completing more sections of your application for better results.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

interface AIProviderCardProps {
  match: MatchResult;
}

const AIProviderCard = ({ match }: AIProviderCardProps) => {
  const matchPercentage = getMatchPercentage(match.matchDetails.overallScore);
  const matchReasons = getMatchReasoning(match);
  
  return (
    <Card className="relative transition-all duration-200 hover:shadow-md border border-primary/10 overflow-hidden">
      <div className={`absolute inset-x-0 h-1 top-0 bg-primary ${
        matchPercentage > 80 ? 'bg-green-500' : 
        matchPercentage > 60 ? 'bg-primary' : 'bg-amber-500'
      }`}></div>
      
      <CardContent className="p-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="font-semibold">{match.provider.name}</div>
          <span className={`text-sm px-2 py-1 rounded-full ${
            matchPercentage > 80 ? 'bg-green-100 text-green-800' : 
            matchPercentage > 60 ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
          }`}>
            {matchPercentage}% Match
          </span>
        </div>
        
        <div className="mt-4 space-y-2">
          <p className="text-sm text-muted-foreground mb-2">Why this is a good match:</p>
          <ul className="space-y-1">
            {matchReasons.map((reason, idx) => (
              <li key={idx} className="text-sm flex items-start gap-2">
                <ThumbsUp className="h-3 w-3 mt-1 text-primary flex-shrink-0" />
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <Button
          variant="outline"
          className="w-full mt-4"
          onClick={() => window.open(`/providers/${match.providerId}`, '_blank')}
        >
          View Details
        </Button>
      </CardContent>
    </Card>
  );
};
